<!-- FieldOperationsPanel.vue - Panel de control operativo -->
<template>
  <div class="field-operations-panel">
    <!-- Resumen de operaciones -->
    <v-row class="mb-4">
      <v-col cols="4">
        <v-card class="text-center pa-2" color="success" variant="outlined">
          <v-card-text>
            <v-icon size="24" class="mb-1">mdi-check-circle</v-icon>
            <div class="text-h6">{{ activeOperations.length }}</div>
            <div class="text-caption">Activas</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="4">
        <v-card class="text-center pa-2" color="warning" variant="outlined">
          <v-card-text>
            <v-icon size="24" class="mb-1">mdi-pause-circle</v-icon>
            <div class="text-h6">{{ pausedOperations.length }}</div>
            <div class="text-caption">Pausadas</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="4">
        <v-card class="text-center pa-2" color="info" variant="outlined">
          <v-card-text>
            <v-icon size="24" class="mb-1">mdi-clock-outline</v-icon>
            <div class="text-h6">{{ plannedOperations.length }}</div>
            <div class="text-caption">Planeadas</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Lista de operaciones -->
    <div class="operations-list">
      <v-card
        v-for="operation in operations"
        :key="operation.id"
        class="mb-3"
        :class="getOperationClass(operation)"
        variant="outlined"
      >
        <v-card-title class="d-flex align-center">
          <v-icon :color="operation.priorityColor" class="mr-2">
            {{ getOperationIcon(operation.type) }}
          </v-icon>
          {{ operation.name }}
          <v-spacer></v-spacer>
          <v-chip
            :color="operation.statusColor"
            size="small"
            variant="flat"
          >
            {{ getStatusText(operation.status) }}
          </v-chip>
        </v-card-title>
        
        <v-card-text>
          <v-row>
            <v-col cols="12" sm="6">
              <div class="text-caption text-medium-emphasis">Ubicación:</div>
              <div class="text-body-2">{{ operation.location.address }}</div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="text-caption text-medium-emphasis">Duración:</div>
              <div class="text-body-2">{{ operation.durationFormatted }}</div>
            </v-col>
          </v-row>

          <!-- Progreso de la operación -->
          <div class="mt-3" v-if="operation.status === 'active'">
            <div class="d-flex justify-space-between mb-1">
              <span class="text-caption">Progreso</span>
              <span class="text-caption">{{ operation.progress }}%</span>
            </div>
            <v-progress-linear
              :model-value="operation.progress"
              color="primary"
              height="6"
              rounded
            ></v-progress-linear>
          </div>

          <!-- Equipo asignado -->
          <div class="mt-2" v-if="operation.assignedTeam">
            <v-chip
              size="small"
              prepend-icon="mdi-account-group"
              variant="outlined"
            >
              {{ operation.assignedTeam }}
            </v-chip>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-btn
            v-if="operation.status === 'planned'"
            @click="$emit('start-operation', operation)"
            color="success"
            size="small"
            prepend-icon="mdi-play"
          >
            Iniciar
          </v-btn>
          
          <v-btn
            v-if="operation.status === 'active'"
            @click="$emit('pause-operation', operation.id)"
            color="warning"
            size="small"
            prepend-icon="mdi-pause"
          >
            Pausar
          </v-btn>
          
          <v-btn
            v-if="operation.status === 'paused'"
            @click="$emit('start-operation', operation)"
            color="success"
            size="small"
            prepend-icon="mdi-play"
          >
            Reanudar
          </v-btn>
          
          <v-btn
            v-if="['active', 'paused'].includes(operation.status)"
            @click="$emit('complete-operation', operation.id)"
            color="primary"
            size="small"
            prepend-icon="mdi-check"
          >
            Completar
          </v-btn>

          <v-spacer></v-spacer>
          
          <v-btn
            icon="mdi-map-marker"
            size="small"
            variant="text"
            @click="showLocationMap(operation)"
          ></v-btn>
        </v-card-actions>
      </v-card>
    </div>

    <!-- Botón para nueva operación -->
    <v-btn
      block
      color="primary"
      prepend-icon="mdi-plus"
      class="mt-4"
      @click="createNewOperation"
    >
      Nueva Operación
    </v-btn>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  operations: {
    type: Array,
    default: () => []
  },
  activeRoutes: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits([
  'start-operation',
  'pause-operation', 
  'complete-operation'
])

// Computed properties
const activeOperations = computed(() => 
  props.operations.filter(op => op.status === 'active')
)

const pausedOperations = computed(() => 
  props.operations.filter(op => op.status === 'paused')
)

const plannedOperations = computed(() => 
  props.operations.filter(op => op.status === 'planned')
)

// Métodos
const getOperationIcon = (type) => {
  const icons = {
    maintenance: 'mdi-wrench',
    inspection: 'mdi-magnify',
    emergency: 'mdi-alert',
    routine: 'mdi-calendar-check'
  }
  return icons[type] || 'mdi-cog'
}

const getStatusText = (status) => {
  const texts = {
    planned: 'Planeada',
    active: 'Activa',
    paused: 'Pausada',
    completed: 'Completada',
    cancelled: 'Cancelada'
  }
  return texts[status] || status
}

const getOperationClass = (operation) => {
  const classes = ['operation-card']
  
  if (operation.priority === 'critical') {
    classes.push('operation-critical')
  } else if (operation.priority === 'high') {
    classes.push('operation-high')
  }
  
  if (operation.status === 'active') {
    classes.push('operation-active')
  }
  
  return classes.join(' ')
}

const showLocationMap = (operation) => {
  console.log('📍 Mostrar ubicación:', operation.location)
  // Implementar mostrar mapa
}

const createNewOperation = () => {
  console.log('➕ Crear nueva operación')
  // Implementar creación de operación
}
</script>

<style scoped>
.field-operations-panel {
  height: 100%;
}

.operations-list {
  max-height: 500px;
  overflow-y: auto;
}

.operation-card {
  transition: all 0.3s ease;
}

.operation-critical {
  border-left: 4px solid #f44336;
  background: rgba(244, 67, 54, 0.05);
}

.operation-high {
  border-left: 4px solid #ff9800;
  background: rgba(255, 152, 0, 0.05);
}

.operation-active {
  box-shadow: 0 0 10px rgba(76, 175, 80, 0.3);
}

.operation-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}
</style>
